import { useBlocker } from '@tanstack/react-router'
import { useCallback, useRef, useState } from 'react'

type InquirySearch = { selectedId?: number }

export function useUnsavedChangesGuard(initialTab: string, guardedTabs: readonly string[]) {
  const isDirtyRef = useRef(false)

  // ルートレベルのブロック
  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) => {
      if (!isDirtyRef.current) return false
      if (current.routeId !== next.routeId) return true
      const { selectedId: currentSelectedId } = current.search as InquirySearch
      const { selectedId: nextSelectedId } = next.search as InquirySearch
      if (currentSelectedId !== nextSelectedId) return true
      return false
    },
    enableBeforeUnload: () => isDirtyRef.current,
    withResolver: true as const,
  })

  // タブレベルのブロック
  const [currentTab, setCurrentTab] = useState(initialTab)
  const [pendingTab, setPendingTab] = useState<string | null>(null)

  const handleDirtyChange = useCallback((dirty: boolean) => {
    isDirtyRef.current = dirty
  }, [])

  const handleTabChange = useCallback(
    (value: string) => {
      if (guardedTabs.includes(currentTab) && isDirtyRef.current) {
        setPendingTab(value)
        return
      }
      setCurrentTab(value)
    },
    [currentTab, guardedTabs],
  )

  const isDialogOpen = blocker.status === 'blocked' || pendingTab !== null

  const handleDiscard = useCallback(() => {
    isDirtyRef.current = false
    if (blocker.status === 'blocked') {
      blocker.proceed?.()
    } else if (pendingTab) {
      setCurrentTab(pendingTab)
      setPendingTab(null)
    }
  }, [blocker, pendingTab])

  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        if (blocker.status === 'blocked') blocker.reset?.()
        else setPendingTab(null)
      }
    },
    [blocker],
  )

  return {
    currentTab,
    handleDirtyChange,
    handleTabChange,
    isDialogOpen,
    handleDiscard,
    handleDialogOpenChange,
  }
}
